import { SelectableItem } from "@/src/components/action-modals/item-selection/item-row";
import { Visibility } from "@/src/components/ui-kit/visibility-section";
import { MIME_TO_EXT } from "@/src/constants/files-extensions";
import { useAuth } from "@/src/contexts/auth.context";
import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import { PickedFile } from "@/src/models/file.models";
import { AttachmentInput, ModalVariant } from "@/src/models/wiki.models";
import {
  NEWS_ALL_QUERY_KEY,
  NEWS_BY_CATEGORY_QUERY_KEY,
  NEWS_CATEGORIES_QUERY_KEY,
  NEWS_DRAFT_QUERY_KEY,
  NEWS_SAVED_QUERY_KEY,
} from "@/src/query-keys/news.keys";
import { USER_PROFILE_QUERY_KEYS } from "@/src/query-keys/users.keys";
import gqlClient from "@/src/services/api/graphql.client";
import { useGetGroups } from "@/src/services/api/groups/groups.hooks";
import { useNewsCategoriesQuery } from "@/src/services/api/news/news.hooks";
import { CREATE_NEWS, UPDATE_NEWS } from "@/src/services/api/news/news.query";
import { sendNotification } from "@/src/services/api/notification/notification.service";
import { useGetUserProfileQuery } from "@/src/services/profile/profile.hooks";
import { UPDATE_USER_PROFILE } from "@/src/services/profile/profile.query";
import { stripHtml } from "@/src/utils/html.utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

export type NewsFormData = Readonly<{
  Title: string;
  Content: string;
  isPinned: boolean;
}>;

const ensureFileName = (
  uri: string,
  fileName?: string | null,
  mimeType?: string | null,
): string => {
  const hasExtension = (name: string) => /\.[a-zA-Z0-9]{1,5}$/.test(name);
  const extFromMime = mimeType ? (MIME_TO_EXT[mimeType] ?? null) : null;

  if (fileName && hasExtension(fileName)) return fileName;
  if (fileName && extFromMime) return `${fileName}.${extFromMime}`;

  const uriSegment = uri.split("/").pop()?.split("?")[0] ?? "";
  if (uriSegment && hasExtension(uriSegment)) return uriSegment;

  const ext = extFromMime ?? "bin";
  return `upload_${Date.now()}.${ext}`;
};

const request = async (query: string, variables?: object) => {
  const client = await gqlClient();
  return client.request(query, variables);
};

const parseParamAttachments = (paramAttachments?: string): PickedFile[] => {
  if (!paramAttachments) return [];
  try {
    const parsed = JSON.parse(paramAttachments);
    return parsed.map((att: any) => ({
      uri: att.url ?? att.attachmentURL,
      fileName: att.name,
      name: att.name,
      mimeType: att.mimeType,
      size: att.size,
      attachmentID: att.id ?? att.attachmentID,
      attachmentURL: att.url ?? att.attachmentURL,
      type: att.mimeType?.startsWith("image/") ? "image" : "file",
    }));
  } catch {
    return [];
  }
};

export function useNewsCreation() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  const { mutateAsync: uploadFile } = useUploadFile();
  const { data: profile } = useGetUserProfileQuery(userId);

  const {
    draftId,
    Title: paramTitle,
    Content: paramContent,
    categoryIds: paramCategoryIds,
    coverImageUrl: paramCoverImage,
    attachments: paramAttachments,
    isPinned: paramIsPinned,
  } = useLocalSearchParams<{
    draftId?: string;
    Title?: string;
    Content?: string;
    categoryIds?: string;
    coverImageUrl?: string;
    attachments?: string;
    isPinned?: string;
  }>();

  const isEditing = !!draftId;

  const { data: groupsData } = useGetGroups();
  const groups: SelectableItem[] = useMemo(
    () =>
      groupsData?.pages?.flatMap((page) =>
        page.getgroups.items.map((group: any) => ({
          itemId: group.ItemId,
          displayName: group.name,
          profileImageUrl: group.coverImageUrl,
        })),
      ) ?? [],
    [groupsData],
  );
  const [visibility, setVisibility] = useState<Visibility>("Public");
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const visibilityRef = useRef(visibility);
  visibilityRef.current = visibility;
  const groupIdsRef = useRef(groupIds);
  groupIdsRef.current = groupIds;
  const [visibilityError, setVisibilityError] = useState<string | undefined>();

  const { data: categoryData = [] } = useNewsCategoriesQuery();
  const categoryDataRef = useRef(categoryData);
  categoryDataRef.current = categoryData;

  const form = useForm<NewsFormData>({
    defaultValues: {
      Title: paramTitle ?? "",
      Content: paramContent ?? "",
      isPinned: paramIsPinned === "true",
    },
  });

  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const selectedCategoryIdsRef = useRef<string[]>([]);
  selectedCategoryIdsRef.current = selectedCategoryIds;

  const [coverImage, setCoverImage] = useState<PickedFile>(
    paramCoverImage ? ({ uri: paramCoverImage } as any) : null,
  );

  const [attachments, setAttachments] = useState<PickedFile[]>(() =>
    parseParamAttachments(paramAttachments),
  );

  useEffect(() => {
    if (!paramCategoryIds || categoryData.length === 0) return;
    try {
      const ids: string[] = JSON.parse(paramCategoryIds);
      const validIds = ids.filter((id) =>
        categoryData.some((cat) => cat.categoryID === id),
      );
      if (validIds.length > 0) setSelectedCategoryIds(validIds);
    } catch {
      const exists = categoryData.some(
        (cat) => cat.categoryID === paramCategoryIds,
      );
      if (exists) setSelectedCategoryIds([paramCategoryIds]);
    }
  }, [paramCategoryIds, categoryData]);

  const [infoModal, setInfoModal] = useState({
    visible: false,
    title: "",
    description: "",
    variant: "info" as ModalVariant,
    onConfirm: () => dismissInfoModal(),
    confirmText: undefined as string | undefined,
  });

  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    title: "",
    description: "",
    confirmText: "",
    cancelText: "",
    onConfirm: () => { },
    onCancel: () => dismissConfirmModal(),
  });

  const dismissInfoModal = () =>
    setInfoModal((prev) => ({ ...prev, visible: false }));

  const dismissConfirmModal = () =>
    setConfirmModal((prev) => ({ ...prev, visible: false }));

  const showInfo = (
    title: string,
    description: string,
    variant: ModalVariant = "info",
    onOk?: () => void,
  ) =>
    setInfoModal({
      visible: true,
      title,
      description,
      variant,
      confirmText: undefined,
      onConfirm: () => {
        dismissInfoModal();
        onOk?.();
      },
    });

  const showConfirm = (
    title: string,
    description: string,
    onConfirm: () => void,
    confirmText = t("BTN_YES"),
    cancelText = t("BTN_NO"),
  ) =>
    setConfirmModal({
      visible: true,
      title,
      description,
      confirmText,
      cancelText,
      onConfirm: () => {
        dismissConfirmModal();
        onConfirm();
      },
      onCancel: dismissConfirmModal,
    });

  const { mutate: submitNews, isPending: isSubmitting } = useMutation({
    mutationFn: async ({
      data,
      isDraft,
      categoryIds,
    }: {
      data: NewsFormData;
      isDraft: boolean;
      categoryIds: string[];
    }) => {
      let coverImageUrl = (coverImage as any)?.uri ?? "";
      if (coverImageUrl && !coverImageUrl.startsWith("http")) {
        const uri = (coverImage as any).uri;
        const mimeType = (coverImage as any).mimeType;
        const rawFileName = (coverImage as any).fileName;

        const uploadedUrl = await uploadFile({
          uri,
          mimeType,
          fileName: ensureFileName(uri, rawFileName, mimeType),
        });

        coverImageUrl = uploadedUrl.split("?")[0];
      }

      const alreadyUploadedAttachments = attachments.filter(
        (file) =>
          file && "uri" in file && (file as any).uri?.startsWith("http"),
      );
      const localAttachments = attachments.filter(
        (file) =>
          file && "uri" in file && !(file as any).uri?.startsWith("http"),
      ) as AttachmentInput[];

      const freshlyUploadedAttachments = await Promise.all(
        localAttachments.map(async (file, index) => {
          const uri = (file as any).uri;
          const mimeType = (file as any).mimeType;
          const rawFileName = (file as any).fileName ?? (file as any).name;

          const uploadedUrl = await uploadFile({
            uri,
            mimeType,
            fileName: ensureFileName(uri, rawFileName, mimeType),
          });

          const url = uploadedUrl.split("?")[0];
          return {
            attachmentID: `att-${Date.now()}-${index}`,
            name: rawFileName || `attachment-${index}`,
            size: (file as any).size ? String((file as any).size) : "0",
            attachmentURL: url,
            mimeType: mimeType || "",
          };
        }),
      );

      const mergedAttachments = [
        ...alreadyUploadedAttachments.map((file: any, index) => ({
          id: file.attachmentID ?? file.id ?? `att-existing-${index}`,
          name: file.name ?? file.fileName ?? "attachment",
          size: file.size ? `${file.size}` : "0",
          url: file.uri ?? file.attachmentURL ?? file.url ?? "",
          mimeType: file.mimeType ?? "",
        })),
        ...freshlyUploadedAttachments.map((att) => ({
          id: att.attachmentID,
          name: att.name,
          size: att.size,
          url: att.attachmentURL,
          mimeType: att.mimeType,
        })),
      ];



      const newsInput = {
        Title: data.Title,
        Content: data.Content,
        coverImageUrl,
        categoryIds,
        attachmentUrls: mergedAttachments,
        isDraft,
        isPinned: data.isPinned,
        visibility: visibilityRef.current,
        groupIds: visibilityRef.current === "Group" ? groupIdsRef.current : [],
        authorSnapshot: {
          firstName: user?.firstName ?? "",
          lastName: user?.lastName ?? "",
          email: user?.email ?? "",
          profileImageUrl: user?.profileImageUrl ?? "",
        },
      };

      let newsItemId: string;

      if (isEditing) {
        await request(UPDATE_NEWS, {
          filter: JSON.stringify({ ItemId: draftId }),
          input: newsInput,
        });
        newsItemId = draftId;
      } else {
        const result: any = await request(CREATE_NEWS, { input: newsInput });
        newsItemId = result?.insertnews?.itemId;
      }

      if (newsItemId && userId) {
        const currentDraftIds = profile?.newsDraftIds ?? [];

        const updatedDraftIds = isDraft
          ? currentDraftIds.includes(newsItemId)
            ? currentDraftIds
            : [...currentDraftIds, newsItemId]
          : currentDraftIds.filter((id) => id !== newsItemId);

        await request(UPDATE_USER_PROFILE, {
          filter: JSON.stringify({ CreatedBy: userId }),
          newsDraftIds: updatedDraftIds,
        });

        queryClient.invalidateQueries({
          queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
        });
      }

      if (!isDraft && newsItemId) {
        await sendNotification({
          userIds: [""],
          configuratoinName: isEditing ? "news-updated" : "news-created",
          denormalizedPayload: JSON.stringify({
            type: isEditing ? "news_article_updated" : "news_article_created",
            message: isEditing
              ? `News article updated: "${data.Title}"`
              : `New news article published: "${data.Title}"`,
            actorAvatar: user?.profileImageUrl ?? "",
            actorName:
              `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim(),
            actorId: user?.itemId ?? "",
            targetEntityId: newsItemId,
          }),
          responseKey: "news-action",
          responseValue: isEditing
            ? "news_article_updated"
            : "news_article_published",
        });
      }
    },

    onSuccess: (_, { isDraft }) => {
      queryClient.invalidateQueries({ queryKey: NEWS_ALL_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_SAVED_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_DRAFT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_BY_CATEGORY_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NEWS_CATEGORIES_QUERY_KEY });
      showInfo(
        t("SUCCESS"),
        isDraft ? t("SUCCESS_NEWS_SAVED_DRAFT") : t("SUCCESS_NEWS_CREATED"),
        "success",
        () => router.replace("/(tabs)/news/latest-news"),
      );
    },

    onError: () => {
      showInfo(t("ERROR"), t("ERROR_GENERIC"), "error");
    },
  });

  const onSubmit = (data: NewsFormData, isDraft: boolean) => {
    if (!isDraft && visibility === "Group" && groupIds.length === 0) {
      setVisibilityError(t("ERROR_SELECT_AT_LEAST_ONE_GROUP"));
      return;
    }
    setVisibilityError(undefined);

    if (isDraft) {
      submitNews({
        data,
        isDraft,
        categoryIds: selectedCategoryIdsRef.current,
      });
      return;
    }

    if (!data.Title?.trim()) {
      showInfo(t("ERROR"), t("ERROR_HEADLINE_BLANK"), "error");
      return;
    }
    if (!stripHtml(data.Content ?? "")) {
      showInfo(t("ERROR"), t("ERROR_CONTENT_BLANK"), "error");
      return;
    }
    if (selectedCategoryIdsRef.current.length === 0) {
      showInfo(t("ERROR"), t("ERROR_CATEGORY_BLANK"), "error");
      return;
    }
    const coverImageUri = (coverImage as any)?.uri ?? "";
    if (!coverImageUri.trim()) {
      showInfo(t("ERROR"), t("ERROR_COVER_IMAGE_BLANK"), "error");
      return;
    }

    submitNews({ data, isDraft, categoryIds: selectedCategoryIdsRef.current });
  };

  const handleCreate = form.handleSubmit((data) => onSubmit(data, false));
  const handleSaveDraft = form.handleSubmit((data) => onSubmit(data, true));

  const handleCancel = () =>
    showConfirm(
      t("BTN_CANCEL"),
      t("QUEST_CANCEL_NEWS_CREATION"),
      () => router.replace("/(tabs)/news/latest-news"),
      t("BTN_YES"),
      t("BTN_CANCEL"),
    );

  const handleAddAttachments = (files: PickedFile[]) =>
    setAttachments((prev) => [...prev, ...files]);

  const handleRemoveAttachment = (index: number) =>
    setAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });

  return {
    form,
    selectedCategoryIds,
    setSelectedCategoryIds,
    visibility,
    setVisibility,
    visibilityError,
    groupIds,
    setGroupIds,
    groups,
    coverImage,
    setCoverImage,
    attachments,
    handleAddAttachments,
    handleRemoveAttachment,
    infoModal,
    confirmModal,
    handleCreate,
    handleSaveDraft,
    handleCancel,
    isEditing,
    isSubmitting,
  };
}
